"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Eye,
  Sparkles,
  Activity,
  Clock,
  Target,
  Search,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TreeBlock } from "@/lib/blocks/tree-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";
import { CounterBlock } from "@/lib/blocks/counter-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const DIMENSIONS = [
  { name: "Dynamic Intelligence", horizon: "Sub-60-second delivery", body: "Immediate response when seconds separate containment from catastrophe. Breaking event detection and threat materialization alerts within seconds." },
  { name: "Operational Intelligence", horizon: "Continuously updated", body: "Day-to-day operations and tactical adjustments with information never more than minutes old. Current-to-the-minute accuracy." },
  { name: "Tactical Intelligence", horizon: "1-4 week horizon", body: "Refining ongoing operations and sharpening execution for the near-term horizon. Message resonance, narrative trajectory, resource allocation." },
  { name: "Strategic Intelligence", horizon: "1-5 year horizon", body: "Multi-year decisions — market entry, political positioning, alliance strategy, investment allocation. Scenario planning with probability-weighting." },
  { name: "Predictive Intelligence", horizon: "Forecasting to 12 months", body: "Preemptive action rather than reactive damage control. Eighty-nine percent viral trajectory prediction. Seeing around corners competitors do not know exist." },
];

const CYCLE = [
  { num: "01", title: "Discovery", body: "Map the complete intelligence landscape. Multi-source monitoring establishes baseline understanding before any collection begins.", accent: "red" },
  { num: "02", title: "Research", body: "Transform raw data into structured understanding. AI processing surfaces non-obvious relationships hidden in billions of data points.", accent: "purple" },
  { num: "03", title: "Monitor", body: "Continuous real-time surveillance. Trusted datasets flow through AI algorithms, eliminating 99.9% of noise before human review.", accent: "cyan" },
  { num: "04", title: "Analysis", body: "Derive actionable intelligence. Predictive models with verified accuracy. Every analysis includes confidence statements and caveats.", accent: "emerald" },
  { num: "05", title: "Execution", body: "Convert intelligence into coordinated action. Counter-narratives deploy before crises crystallize. Real-time adjustment of parameters.", accent: "amber-gold" },
];

const EVIDENCE = [
  { metric: "89", suffix: "%", label: "Crisis Prediction", sub: "Verified accuracy", accent: "red" },
  { metric: "60", suffix: "s", label: "Alert Latency", sub: "Under sixty seconds", accent: "purple" },
  { metric: "87", suffix: "%", label: "Booth-Level Accuracy", sub: "Ground truth", accent: "cyan" },
  { metric: "500", suffix: "M+", label: "Daily Data Points", sub: "Across 195 countries", accent: "emerald" },
];

const PIPELINE = [
  "Data Collection", "Normalization", "Deduplication", "Entity Extraction", "Sentiment Scoring",
  "Trend Detection", "Alert Generation", "Narrative Clustering", "Intelligence Synthesis", "Report Generation",
];

const APPLICATIONS = [
  { title: "Sovereign Application", body: "Governments, defense agencies, and heads of state. Multi-agency intelligence fusion, classified integration, geopolitical early warning. The intelligence cycle operates at multiple tempos simultaneously.", accent: "red" },
  { title: "Enterprise Application", body: "Fortune-scale corporations. Competitive intelligence, market intelligence, M&A evaluation, regulatory monitoring, executive threat assessment. Decision-support architecture, not a monitoring dashboard.", accent: "purple" },
  { title: "Political Application", body: "Electoral intelligence. Booth-level voter prediction at 87% accuracy. Fifteen victories in eighteen engagements. Nine hundred million-plus voters influenced. The integration between digital and ground-truth intelligence.", accent: "cyan" },
];

export default function StrategicIntelligencePage() {
  return (
    <>
      <PageHero
        icon={Eye}
        overline="Strategic Intelligence"
        overlineClassName="text-purple"
        title={
          <>
            Seeing What Others <span className="text-gradient-premium">Cannot See.</span>
          </>
        }
        subtitle="Strategic intelligence is not a product. It is a capability — the capacity to see what others cannot, to act on what others have not detected, and to win where others do not yet know a contest exists. The intelligence architecture Dr. Jyoti Kush oversees was forged where intelligence failure was measured in national stability."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Strategic Intelligence" }]}
        badge="The prerequisite for survival"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">The Intelligence Imperative</span>
            </div>
            <h2 className="text-display-md mb-5">
              Intelligence asymmetry is the single greatest competitive advantage.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Every catastrophic organizational failure can be traced to a single root cause: intelligence failure. Not insufficient data. Not inadequate resources. The absence of intelligence — the systematic derivation of decision-ready understanding from the noise of an information-saturated world.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The architecture Dr. Jyoti Kush commands processes over five hundred million data points daily, monitors two hundred-plus digital platforms across one hundred ninety-five countries, and delivers decision-ready intelligence at the tempo of sovereign operations. The ten-stage signal-to-intelligence refinement pipeline filters noise with ninety-nine-point-nine percent efficiency.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Clock className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Five-Dimensional Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Intelligence across every decision horizon.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The framework is not a ladder but a mesh. Intelligence flows continuously between dimensions — ensuring tactical decisions are informed by strategic context.
          </p>
        </div>
        <div className="space-y-3">
          {DIMENSIONS.map((d, i) => {
            const a = ACCENT[["red", "purple", "cyan", "emerald", "amber-gold"][i]];
            return (
              <motion.div
                key={d.name}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
                  <div className={`size-14 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-body-md font-bold text-white">{`0${i + 1}`}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{d.name}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                  <div className="hidden lg:block text-right shrink-0 max-w-[180px]">
                    <div className="text-caption font-semibold text-muted-foreground uppercase tracking-wider">{d.horizon}</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Activity className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Intelligence Cycle</span>
          </div>
          <h2 className="text-display-md mb-4">Five stages. Refined through hundreds of engagements.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every intelligence operation follows this cycle. The loop never closes. Each iteration is smarter than the last.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CYCLE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{s.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{s.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Search className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold\">The Refinement Pipeline</span>
          </div>
          <h2 className="text-display-md mb-4">Ten stages from data to decision-ready intelligence.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            No data reaches a decision-maker without completing the full refinement. The pipeline is a transformation engine, converting noise into understanding.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {PIPELINE.map((stage, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim", "lime", "coral", "red", "purple"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={stage}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.3, delay: i * 0.03 }}
                className={`relative glass-card rounded-2xl p-4 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className={`text-caption font-bold ${a.text} opacity-50 leading-none mb-2`}>{`Stage ${String(i + 1).padStart(2, "0")}`}</div>
                <h3 className={`text-caption font-bold ${a.text}`}>{stage}</h3>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Target className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Three Application Domains</span>
          </div>
          <h2 className="text-display-md mb-4">From sovereign operations to enterprise intelligence.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The intelligence architecture adapts to the altitude of the engagement — from national-scale intelligence fusion to corporate competitive positioning.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {APPLICATIONS.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${ac.text}`}>{a.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="intelligence-tree" ariaLabel="Intelligence Decision Tree">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <BookOpen className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Intelligence Decision Tree</span>
          </div>
          <h2 className="text-display-md mb-4">Five roots. One sovereign decision system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The intelligence architecture is a tree, not a stack. Five root domains, each branching into operational capabilities, each feeding decision-ready output into the engagement layer above.
          </p>
        </div>
        <TreeBlock
          entries={[
            {
              id: "root-1",
              label: "Signal Intelligence",
              body: "The capacity to detect the signal inside the noise. Sub-sixty-second alert latency, multi-language, multi-platform ingestion.",
              accent: "red",
              children: [
                { id: "sig-1", label: "Multi-Platform Monitoring", body: "Two hundred-plus digital platforms and one hundred thousand-plus news sources across fifty-plus languages.", accent: "red" },
                { id: "sig-2", label: "Dark-Web Surveillance", body: "One thousand-plus dark-web sources for threat detection before public materialization.", accent: "red" },
                { id: "sig-3", label: "Cross-Platform Correlation", body: "Entity resolution that links identities across surface, deep, and dark channels.", accent: "red" },
              ],
            },
            {
              id: "root-2",
              label: "Ground-Truth Intelligence",
              body: "Booth-level voter prediction at eighty-seven percent accuracy. Eight hundred forty-seven-plus electoral cycles across seventy-three countries.",
              accent: "purple",
              children: [
                { id: "gt-1", label: "Booth-Level Prediction", body: "Twenty-six points higher than digital-only methods. Verified across sovereign-scale engagements.", accent: "purple" },
                { id: "gt-2", label: "Constituency Mapping", body: "Five-layer intelligence matrix covering demography, sentiment, historical swing, demographics, and digital reach.", accent: "purple" },
              ],
            },
            {
              id: "root-3",
              label: "Crisis Intelligence",
              body: "The early-warning system. Eighty-nine percent crisis prediction accuracy, predictive analytics, sentiment volatility tracking.",
              accent: "cyan",
              children: [
                { id: "cr-1", label: "Predictive Crisis Modeling", body: "Seven-day and thirty-day forecasts with confidence statements. Preemptive, not reactive.", accent: "cyan" },
                { id: "cr-2", label: "Sentiment Recovery Curves", body: "Five recovery patterns. The right curve selected per crisis. The wrong curve multiplies damage.", accent: "cyan" },
                { id: "cr-3", label: "Influencer Defense Network", body: "Eight hundred fifty thousand-plus profiled advocates, ready for rapid narrative activation.", accent: "cyan" },
              ],
            },
            {
              id: "root-4",
              label: "Narrative Intelligence",
              body: "The capability to engineer perception. Ninety-five to ninety-eight percent organic authenticity. Three hundred to five hundred percent narrative penetration.",
              accent: "emerald",
              children: [
                { id: "nar-1", label: "Content Engines", body: "Four core engines producing one thousand to one hundred thousand-plus variants per campaign.", accent: "emerald" },
                { id: "nar-2", label: "Dissemination Architecture", body: "One thousand-plus syndication points. Seventeen platforms in under five minutes.", accent: "emerald" },
              ],
            },
            {
              id: "root-5",
              label: "Security Intelligence",
              body: "Sovereign-grade protection of the intelligence layer. Zero-trust, post-quantum, military-grade encryption, zero incidents across fifteen-plus years.",
              accent: "amber-gold",
              children: [
                { id: "sec-1", label: "Sovereign Encryption", body: "Five integrated encryption products across the CryptoSuite. Quantum-resistant by design.", accent: "amber-gold" },
                { id: "sec-2", label: "Air-Gap Capability", body: "Operations continue without internet exposure when required by threat environment.", accent: "amber-gold" },
              ],
            },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Activity className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Verified Outcomes</span>
          </div>
          <h2 className="text-display-md mb-4">Numbers the record cannot dispute.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data. Every number is verified, not projected. The record speaks with the authority of sovereign-scale deployments.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 89, suffix: "%", label: "Crisis Prediction Accuracy", description: "Verified across hundreds of deployments", accent: "red" },
            { value: 60, suffix: "s", label: "Alert Latency", description: "Sub-sixty-second delivery when seconds count", accent: "purple" },
            { value: 87, suffix: "%", label: "Booth-Level Accuracy", description: "Twenty-six points above digital-only baselines", accent: "cyan" },
            { value: 500, suffix: "M+", label: "Daily Data Points", description: "Across one hundred ninety-five countries", accent: "emerald" },
            { value: 200, suffix: "+", label: "Platforms Monitored", description: "Surface, deep, and dark channels in fifty-plus languages", accent: "amber-gold" },
            { value: 10000, suffix: "+", label: "Requests Per Second", description: "Sustained processing capacity at peak load", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Layers className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Disciplines Within the Intelligence Practice</span>
          </div>
          <h2 className="text-display-md mb-4">The seven disciplines every operator masters.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Strategic intelligence is not a single capability. It is a federation of disciplines — each requiring distinct tooling, distinct training, and distinct verification regimes.
          </p>
        </div>
        <CardGridBlock
          columns={3}
          items={[
            { title: "Geopolitical Intelligence", body: "Long-cycle state and non-state actor analysis. Twelve-month forecasting with probability weighting.", icon: <Target className="size-5" />, accent: "red" },
            { title: "Competitive Intelligence", body: "Market structure, competitor capability, supply chain resilience. Seventy-five percent-plus move prediction.", icon: <Activity className="size-5" />, accent: "purple" },
            { title: "Narrative Intelligence", body: "Story velocity, resonance, decay. The physics of how ideas move through populations.", icon: <Sparkles className="size-5" />, accent: "cyan" },
            { title: "Reputation Intelligence", body: "Brand, leader, institution — continuous measurement across one hundred forty-four perception dimensions.", icon: <Eye className="size-5" />, accent: "emerald" },
            { title: "Threat Intelligence", body: "Adversary capability, intent, opportunity. The triad that determines whether a threat materializes.", icon: <Activity className="size-5" />, accent: "amber-gold" },
            { title: "Electoral Intelligence", body: "Booth-level mapping, swing analysis, demographic derivation. Eighty-seven percent accuracy across eight hundred forty-seven-plus cycles.", icon: <Target className="size-5" />, accent: "denim" },
            { title: "Crisis Intelligence", body: "Pre-crisis indicators, real-time amplification dynamics, recovery curve selection. The discipline of staying ahead.", icon: <Clock className="size-5" />, accent: "coral" },
            { title: "Influence Intelligence", body: "Network analysis, node identification, amplification pathways. The map of who moves whom.", icon: <Layers className="size-5" />, accent: "lime" },
            { title: "Sovereign Security Intelligence", body: "Signals intelligence, cyber threat, counter-intelligence, and the disciplines that protect the intelligence layer itself.", icon: <Activity className="size-5" />, accent: "purple" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-purple mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Activate the intelligence architecture.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Sovereign-grade intelligence for governments, corporations, and political organizations. Every engagement begins with diagnosis. Every outcome is measured.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Eye, accent: "purple" },
          { title: "Political Strategy", description: "Engineering electoral outcomes. Booth-level intelligence and verified campaign victories.", href: "/expertise/political-strategy/", icon: Target, accent: "cyan" },
          { title: "Crisis Management", description: "The crisis transformation engine. Compressing damage windows and recovering in 30-60 days.", href: "/expertise/crisis-management/", icon: Activity, accent: "emerald" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
